import {
  WORLD_PROTOCOL,
  WORLD_TICKET_SUBPROTOCOL_PREFIX,
  parseWorldServerMessage,
  type WorldMember,
  type WorldPose,
  type WorldServerMessage,
} from '../../shared/worldProtocol'

export interface WorldPresenceTransport {
  connect(
    onRoster: (members: WorldMember[]) => void,
    onConnectionState: (status: PresenceConnectionStatus) => void,
  ): void
  updatePose(pose: Omit<WorldPose, 'animation'>): void
  changeRoom(roomId: string): void
  disconnect(): void
}

export type PresenceConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'ready'
  | 'degraded'

interface SessionPayload {
  authenticated: true
  csrfToken: string
}

interface WorldTicketPayload {
  protocol: typeof WORLD_PROTOCOL
  ticket: string
  worldId: string
  expiresAt: string
}

export interface BrowserWorldClientOptions {
  roomId?: string
  webSocketFactory?: (url: string, protocols: string[]) => WebSocket
  fetchImpl?: typeof fetch
}

export function inferPresenceAnimation(
  previous: Omit<WorldPose, 'animation'> | null,
  next: Omit<WorldPose, 'animation'>,
): WorldPose['animation'] {
  if (!previous) return 'idle'
  return Math.hypot(next.x - previous.x, next.z - previous.z) > 0.01
    ? 'walking'
    : 'idle'
}

export function classifyWorldMessageOrder(
  lastEventId: number,
  lastRevision: number,
  message: WorldServerMessage,
): 'accept' | 'ignore' | 'resync' {
  if (message.type === 'world.snapshot') return 'accept'
  if (message.eventId <= lastEventId) return 'ignore'
  if (
    message.type === 'presence.upsert' ||
    message.type === 'presence.remove'
  ) {
    if (message.revision <= lastRevision) return 'ignore'
    if (message.revision !== lastRevision + 1) return 'resync'
  }
  return 'accept'
}

/** Versioned, same-origin client for Arena-owned ephemeral world state. */
export class BrowserWorldClient implements WorldPresenceTransport {
  private socket: WebSocket | null = null
  private desired = false
  private sequence = 0
  private roomId: string
  private reconnectAttempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private onRoster: (members: WorldMember[]) => void = () => {}
  private onConnectionState: (status: PresenceConnectionStatus) => void = () => {}
  private connectionStatus: PresenceConnectionStatus = 'disconnected'
  private readonly members = new Map<string, WorldMember>()
  private selfPlayerId: string | null = null
  private previousPose: Omit<WorldPose, 'animation'> | null = null
  private generation = 0
  private lastEventId = 0
  private lastRevision = 0

  constructor(private readonly options: BrowserWorldClientOptions = {}) {
    this.roomId = options.roomId ?? 'district'
  }

  connect(
    onRoster: (members: WorldMember[]) => void,
    onConnectionState: (status: PresenceConnectionStatus) => void,
  ): void {
    this.desired = true
    this.onRoster = onRoster
    this.onConnectionState = onConnectionState
    const generation = ++this.generation
    this.setConnectionStatus('connecting')
    void this.open(generation)
  }

  updatePose(pose: Omit<WorldPose, 'animation'>): void {
    const previous = this.previousPose
    const animation = inferPresenceAnimation(previous, pose)
    this.previousPose = { ...pose }
    this.send({
      protocol: WORLD_PROTOCOL,
      type: 'presence.update',
      messageId: crypto.randomUUID(),
      sequence: this.sequence++,
      pose: { ...pose, animation },
    })
  }

  changeRoom(roomId: string): void {
    if (roomId === this.roomId) return
    this.roomId = roomId
    this.send({
      protocol: WORLD_PROTOCOL,
      type: 'room.change',
      messageId: crypto.randomUUID(),
      sequence: this.sequence++,
      roomId,
    })
  }

  disconnect(): void {
    this.desired = false
    this.generation += 1
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    const socket = this.socket
    this.socket = null
    socket?.close(1000, 'left world')
    this.members.clear()
    this.previousPose = null
    this.lastEventId = 0
    this.lastRevision = 0
    this.onRoster([])
    this.setConnectionStatus('disconnected')
  }

  private async open(generation: number): Promise<void> {
    try {
      const fetchImpl = this.options.fetchImpl ?? fetch
      const response = await fetchImpl('/api/v1/session', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error(`world session failed (${response.status})`)
      const session = await response.json() as SessionPayload
      if (!this.desired || generation !== this.generation) return
      if (!session.authenticated || !session.csrfToken) {
        throw new Error('world session invalid')
      }

      const ticketResponse = await fetchImpl('/api/v1/world-ticket', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Arena-CSRF': session.csrfToken,
        },
      })
      if (!ticketResponse.ok) {
        throw new Error(`world ticket failed (${ticketResponse.status})`)
      }
      const ticket = await ticketResponse.json() as WorldTicketPayload
      if (!this.desired || generation !== this.generation) return
      if (
        ticket.protocol !== WORLD_PROTOCOL ||
        !ticket.ticket ||
        !ticket.worldId
      ) throw new Error('world ticket invalid')

      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url = `${scheme}//${window.location.host}/api/world/v1`
      const protocols = [
        WORLD_PROTOCOL,
        `${WORLD_TICKET_SUBPROTOCOL_PREFIX}${ticket.ticket}`,
      ]
      const socket = (
        this.options.webSocketFactory ??
        ((value, requestedProtocols) => new WebSocket(value, requestedProtocols))
      )(url, protocols)
      this.socket = socket
      socket.addEventListener('open', () => {
        if (
          !this.desired ||
          generation !== this.generation ||
          this.socket !== socket
        ) {
          socket.close()
          return
        }
        this.sequence = 0
        const resumeFrom = this.lastEventId > 0
          ? this.lastEventId
          : undefined
        socket.send(JSON.stringify({
          protocol: WORLD_PROTOCOL,
          type: 'world.join',
          messageId: crypto.randomUUID(),
          worldId: ticket.worldId,
          roomId: this.roomId,
          ...(resumeFrom === undefined ? {} : { resumeFrom }),
        }))
      })
      socket.addEventListener('message', (event) => {
        if (
          !this.desired ||
          generation !== this.generation ||
          this.socket !== socket
        ) return
        if (typeof event.data !== 'string') return
        let decoded: unknown
        try {
          decoded = JSON.parse(event.data)
        } catch {
          socket.close(4400, 'invalid world message')
          return
        }
        const parsed = parseWorldServerMessage(decoded)
        if (!parsed.ok || !parsed.value) {
          socket.close(4400, 'invalid world message')
          return
        }
        const message = parsed.value
        const order = classifyWorldMessageOrder(
          this.lastEventId,
          this.lastRevision,
          message,
        )
        if (order === 'ignore') return
        if (order === 'resync') {
          socket.close(4410, 'world projection gap')
          return
        }
        if (message.type === 'world.snapshot') {
          this.reconnectAttempt = 0
          this.lastEventId = message.eventId
          this.lastRevision = message.revision
          this.selfPlayerId = message.selfPlayerId
          this.members.clear()
          for (const member of message.members) {
            if (member.playerId !== this.selfPlayerId) {
              this.members.set(member.playerId, member)
            }
          }
          this.emitRoster()
          this.setConnectionStatus('ready')
        } else if (message.type === 'presence.upsert') {
          this.lastEventId = message.eventId
          this.lastRevision = message.revision
          if (message.member.playerId !== this.selfPlayerId) {
            this.members.set(message.member.playerId, message.member)
            this.emitRoster()
          }
        } else if (message.type === 'presence.remove') {
          this.lastEventId = message.eventId
          this.lastRevision = message.revision
          if (this.members.delete(message.playerId)) this.emitRoster()
        } else {
          this.lastEventId = message.eventId
        }
      })
      socket.addEventListener('close', () => {
        if (
          !this.desired ||
          generation !== this.generation ||
          this.socket !== socket
        ) return
        this.socket = null
        // A disconnected presence projection is unknown, not a frozen roster.
        // Preserve the event cursor for resume, but remove avatars immediately.
        this.members.clear()
        this.selfPlayerId = null
        this.emitRoster()
        this.setConnectionStatus('degraded')
        this.scheduleReconnect(generation)
      })
    } catch {
      if (this.desired && generation === this.generation) {
        this.setConnectionStatus('degraded')
        this.scheduleReconnect(generation)
      }
    }
  }

  private scheduleReconnect(generation: number): void {
    if (this.reconnectTimer !== null) return
    const delay = Math.min(10_000, 250 * 2 ** this.reconnectAttempt++)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.desired && generation === this.generation) {
        this.setConnectionStatus('connecting')
        void this.open(generation)
      }
    }, delay)
  }

  private send(message: object): void {
    if (this.socket?.readyState === 1) {
      this.socket.send(JSON.stringify(message))
    }
  }

  private emitRoster(): void {
    this.onRoster([...this.members.values()])
  }

  private setConnectionStatus(status: PresenceConnectionStatus): void {
    if (this.connectionStatus === status) return
    this.connectionStatus = status
    this.onConnectionState(status)
  }
}
